import React, { useLayoutEffect, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import RichTextInput from '../../common/RichTextInput'
import Dropdown from '../../common/Dropdown'
import { TrashCanFull } from '../../../icons'
import { useWindowSize } from '../../../libs/hooks'

const FormProductDescription = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const { productId, descriptionFormData, productDescriptionErrors, resetFeaturesErrors, updateDescription } = props
  const [relatedProductOptions, setRelatedProductOptions] = useState([])

  useLayoutEffect(() => {
    if (descriptionFormData.features.length === 0) {
      updateDescription({ ...descriptionFormData, features: [{ title: '', details: '' }] })
    }
  }, [descriptionFormData.features])

  useEffect(() => {
    if (props.products.length > 0) {
      const mappedProducts = props.products.filter((p) => p.id !== productId).map((p) => ({ id: p.id, name: p.title }))
      setRelatedProductOptions(mappedProducts)
    }
  }, [props.products])

  const selectRelatedItem = (relatedItemId, idx) => {
    const newRelatedProductIds = [...descriptionFormData.relatedProductIds]
    newRelatedProductIds[idx] = relatedItemId

    updateDescription({ ...descriptionFormData, relatedProductIds: newRelatedProductIds })
  }

  const removeRelatedItem = (idx) => {
    const newRelatedProductIds = [...descriptionFormData.relatedProductIds]
    newRelatedProductIds[idx] = null

    updateDescription({ ...descriptionFormData, relatedProductIds: newRelatedProductIds })
  }

  const handleUpdateFeatureTitle = (value, index) => {
    const newFeatures = descriptionFormData.features.map((feature, i) => {
      if (i === index) {
        return { ...feature, title: value }
      }
      return feature
    })

    updateDescription({ ...descriptionFormData, features: newFeatures })
  }

  const handleUpdateFeatureDetails = (value, index) => {
    const newFeatures = descriptionFormData.features.map((feature, i) => {
      if (i === index) {
        return { ...feature, details: value }
      }
      return feature
    })

    updateDescription({ ...descriptionFormData, features: newFeatures })
  }

  const handleDeleteFeature = (index) => {
    const newFeatures = descriptionFormData.features.filter((feature, i) => i !== index)

    resetFeaturesErrors()
    updateDescription({ ...descriptionFormData, features: newFeatures })
  }

  const handleAddFeature = () => {
    const newFeatures = [...descriptionFormData.features, { title: '', details: '' }]

    updateDescription({ ...descriptionFormData, features: newFeatures })
  }

  return (
    <div className="product-description-wrapper">
      <div className="fixed-input-wrapper without-fixed-height">
        <div className="section-subtitle with-margin-bottom">{t('description')}</div>
        <textarea
          className={'text-area-input' + (productDescriptionErrors?.description ? ' input-error' : '')}
          value={descriptionFormData.productDescription}
          onChange={(e) => updateDescription({ ...descriptionFormData, productDescription: e.target.value })}
        />
      </div>
      <div className="internal-section-delimiter" />
      <div className="section-subtitle">{t('coltene_store.create_product.features')}</div>
      {descriptionFormData.features.map((feature, index) => (
        <div key={index} className={`product-options-wrapper flex-column`}>
          <div className="fixed-input-wrapper without-fixed-height">
            <div className="input-label">{t('title')}</div>
            <div className="title-with-action">
              <input
                type="text"
                className={'input-item' + (productDescriptionErrors?.features?.includes(`${index}-title`) ? ' input-error' : '')}
                value={feature.title}
                onChange={(e) => handleUpdateFeatureTitle(e.target.value, index)}
              />
              <div className="delete-option-wrapper">
                <TrashCanFull
                  onClick={() => {
                    handleDeleteFeature(index)
                  }}
                />
              </div>
            </div>
          </div>
          <div className="fixed-input-wrapper without-fixed-height">
            <div className="input-label">{t('details')}</div>
            <RichTextInput
              value={feature.details}
              onChangeValue={(value) => handleUpdateFeatureDetails(value, index)}
              hasError={productDescriptionErrors?.features?.includes(`${index}-details`)}
              maxLength={1000}
            />
          </div>
        </div>
      ))}
      <div className="add-option-wrapper" onClick={() => handleAddFeature()}>
        {`+  ${t('coltene_store.create_product.add-new-feature')}`}
      </div>
      <div className="internal-section-delimiter" />
      <div className="fixed-input-wrapper without-fixed-height">
        <div className="section-subtitle with-margin-bottom">{t('coltene_store.create_product.specifications')}</div>
        <RichTextInput
          value={descriptionFormData.specification}
          onChangeValue={(value) => updateDescription({ ...descriptionFormData, specification: value })}
          hasError={productDescriptionErrors?.specification}
          maxLength={2000}
        />
      </div>
      <div className="internal-section-delimiter" />
      <div className="section-subtitle with-margin-bottom">{t('coltene_store.create_product.accessories')}</div>
      <div className={`product-options-wrapper ${isMobile && 'flex-column'}`}>
        <div className="fixed-input-wrapper one-third-wrapper">
          <div className="input-label">{`${t('coltene_store.create_product.item')} 1`}</div>
          <Dropdown
            options={relatedProductOptions.map((option) => ({
              key: option.id,
              value: option.name,
              isDisabled: descriptionFormData.relatedProductIds.includes(option.id),
            }))}
            initialSelectedKey={descriptionFormData.relatedProductIds[0]}
            onChange={(item) => selectRelatedItem(item.key, 0)}
            onOptionReset={() => removeRelatedItem(0)}
            hasError={productDescriptionErrors?.relatedProductIds?.includes(0)}
          />
        </div>
        <div className="fixed-input-wrapper one-third-wrapper">
          <div className="input-label">{`${t('coltene_store.create_product.item')} 2`}</div>
          <Dropdown
            options={relatedProductOptions.map((option) => ({
              key: option.id,
              value: option.name,
              isDisabled: descriptionFormData.relatedProductIds.includes(option.id),
            }))}
            initialSelectedKey={descriptionFormData.relatedProductIds[1]}
            onChange={(item) => selectRelatedItem(item.key, 1)}
            onOptionReset={() => removeRelatedItem(1)}
            hasError={productDescriptionErrors?.relatedProductIds?.includes(1)}
          />
        </div>
        <div className="fixed-input-wrapper one-third-wrapper">
          <div className="input-label">{`${t('coltene_store.create_product.item')} 3`}</div>
          <Dropdown
            options={relatedProductOptions.map((option) => ({
              key: option.id,
              value: option.name,
              isDisabled: descriptionFormData.relatedProductIds.includes(option.id),
            }))}
            initialSelectedKey={descriptionFormData.relatedProductIds[2]}
            onChange={(item) => selectRelatedItem(item.key, 2)}
            onOptionReset={() => removeRelatedItem(2)}
            hasError={productDescriptionErrors?.relatedProductIds?.includes(2)}
          />
        </div>
      </div>
    </div>
  )
}

function mapStateToProps({ adminStore }) {
  return {
    products: adminStore?.products || [], //A.S: Possible issue on not having retrieved all products
  }
}

export default connect(mapStateToProps)(FormProductDescription)

